import { Store, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';

/**
 * 엔티티 카드 테마 타입
 * - organization: 녹색 그라디언트
 * - brand: 보라색 그라디언트
 */
type EntityCardTheme = 'organization' | 'brand';

const THEME_COLORS = {
  organization: {
    gradient: 'from-primary-hover to-primary',
    accent: 'text-primary',
    iconColor: 'text-primary',
  },
  brand: {
    gradient: 'from-secondary to-secondary-light',
    accent: 'text-secondary',
    iconColor: 'text-secondary',
  },
} as const;

interface EntityCardProps {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  logoUrl?: string | null;
  storesCount?: number;
  isActive?: boolean;
  theme?: EntityCardTheme;
  onClick?: (slug: string) => void;
}

/**
 * 재사용 가능한 엔티티 카드 컴포넌트
 * 조직, 브랜드 등 다양한 엔티티에 사용 가능
 */
export function EntityCard({
  slug,
  name,
  description,
  logoUrl,
  storesCount = 0,
  isActive = true,
  theme = 'organization',
  onClick,
}: EntityCardProps) {
  const colors = THEME_COLORS[theme];

  return (
    <div
      className={`bg-background rounded-[8px] shadow-sm border border-foreground-muted/10
      p-4 mb-3 max-w-full overflow-hidden
      transition-all duration-200 ease-in-out ${
        isActive
          ? 'cursor-pointer hover:shadow-md hover:shadow-primary/10 active:scale-[0.98] hover:border-primary/20'
          : 'opacity-60 cursor-not-allowed'
      }`}
      onClick={() => isActive && onClick?.(slug)}
    >
      <div className="flex gap-[min(3vw,1rem)]">
        {/* Logo */}
        <div className="relative w-[calc(4rem+2vw)] h-[calc(4rem+2vw)] shrink-0">
          {logoUrl ? (
            <ImageWithFallback
              src={logoUrl}
              alt={name}
              className="w-full h-full object-cover rounded-[5px]"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${colors.gradient} rounded-[5px] flex items-center justify-center`}>
              <span className="text-white text-[calc(1.5rem+0.5vw)] font-bold">
                {(name || '').charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 h-[calc(4rem+2vw)] flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] min-[600px]:text-base font-medium text-foreground truncate">
                {name}
              </h3>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
          </div>

          {description && (
            <p className="text-[11px] min-[600px]:text-[12px] text-muted-foreground line-clamp-2 overflow-hidden">
              {description}
            </p>
          )}

          <div className="flex items-center gap-1">
            <Store className={`h-3 w-3 ${colors.iconColor}`} />
            <span className={`text-[calc(0.75rem+0.125vw)] ${colors.accent} font-medium`}>
              {storesCount} {storesCount === 1 ? 'sucursal' : 'sucursales'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
